import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import ClearIcon from "@mui/icons-material/Clear";
import {
  Box,
  ClickAwayListener,
  IconButton,
  InputAdornment,
  Paper,
  TextField,
  Typography,
} from "@mui/material";
import { useEffect, useRef, useState } from "react";

interface CustomDatePickerProps {
  label: string;
  value: string; // "YYYY-MM-DD"
  onChange: (value: string) => void;
  minDate?: string; // "YYYY-MM-DD"
  maxDate?: string; // "YYYY-MM-DD"
}

const DAYS = ["Su", "Mo", "Tu", "We", "Th", "Fr", "Sa"];
const MONTHS = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December",
];

function formatDisplay(value: string) {
  if (!value) return "";
  const [y, m, d] = value.split("-");
  return `${d}-${m}-${y}`;
}

function toDateParts(value: string): { year: number; month: number; day: number } | null {
  if (!value) return null;
  const [y, m, d] = value.split("-").map(Number);
  if (!y || !m || !d) return null;
  return { year: y, month: m - 1, day: d };
}

function isoString(year: number, month: number, day: number) {
  return `${year}-${String(month + 1).padStart(2, "0")}-${String(day).padStart(2, "0")}`;
}

export function CustomDatePicker({ label, value, onChange, minDate, maxDate }: CustomDatePickerProps) {
  const today = new Date();
  const parsed = toDateParts(value);

  const [open, setOpen] = useState(false);
  const [viewYear, setViewYear] = useState(parsed?.year ?? today.getFullYear());
  const [viewMonth, setViewMonth] = useState(parsed?.month ?? today.getMonth());
  const anchorRef = useRef<HTMLDivElement>(null);
  const [dropUp, setDropUp] = useState(false);

  useEffect(() => {
    if (open && anchorRef.current) {
      const rect = anchorRef.current.getBoundingClientRect();
      const spaceBelow = window.innerHeight - rect.bottom;
      setDropUp(spaceBelow < 260);
    }
  }, [open]);

  const calendarDays = () => {
    const firstDay = new Date(viewYear, viewMonth, 1).getDay();
    const daysInMonth = new Date(viewYear, viewMonth + 1, 0).getDate();
    const prevDays = new Date(viewYear, viewMonth, 0).getDate();
    const cells: { day: number; currentMonth: boolean; iso: string }[] = [];

    for (let i = firstDay - 1; i >= 0; i--) {
      const d = prevDays - i;
      const m = viewMonth - 1 < 0 ? 11 : viewMonth - 1;
      const y = viewMonth - 1 < 0 ? viewYear - 1 : viewYear;
      cells.push({ day: d, currentMonth: false, iso: isoString(y, m, d) });
    }
    for (let d = 1; d <= daysInMonth; d++) {
      cells.push({ day: d, currentMonth: true, iso: isoString(viewYear, viewMonth, d) });
    }
    const remaining = 42 - cells.length;
    for (let d = 1; d <= remaining; d++) {
      const m = viewMonth + 1 > 11 ? 0 : viewMonth + 1;
      const y = viewMonth + 1 > 11 ? viewYear + 1 : viewYear;
      cells.push({ day: d, currentMonth: false, iso: isoString(y, m, d) });
    }
    return cells;
  };

  const isDisabled = (iso: string) => {
    if (minDate && iso < minDate) return true;
    if (maxDate && iso > maxDate) return true;
    return false;
  };

  const isToday = (iso: string) => {
    return iso === isoString(today.getFullYear(), today.getMonth(), today.getDate());
  };

  const handleSelect = (iso: string) => {
    if (isDisabled(iso)) return;
    onChange(iso);
    setOpen(false);
  };

  const handleClear = (e: React.MouseEvent) => {
    e.stopPropagation();
    onChange("");
  };

  const prevMonth = () => {
    if (viewMonth === 0) { setViewMonth(11); setViewYear((y) => y - 1); }
    else setViewMonth((m) => m - 1);
  };

  const nextMonth = () => {
    if (viewMonth === 11) { setViewMonth(0); setViewYear((y) => y + 1); }
    else setViewMonth((m) => m + 1);
  };

  const goToday = () => {
    setViewYear(today.getFullYear());
    setViewMonth(today.getMonth());
  };

  const cells = calendarDays();

  return (
    <ClickAwayListener onClickAway={() => setOpen(false)}>
      <Box ref={anchorRef} sx={{ position: "relative", display: "inline-flex" }}>
        <TextField
          size="small"
          label={label}
          value={formatDisplay(value)}
          placeholder="dd-mm-yyyy"
          onClick={() => setOpen((o) => !o)}
          InputLabelProps={{ shrink: true, sx: { fontSize: "13px" } }}
          InputProps={{
            readOnly: true,
            sx: { fontSize: "13px", borderRadius: "8px", bgcolor: "#fff", cursor: "pointer" },
            startAdornment: (
              <InputAdornment position="start">
                <CalendarTodayIcon sx={{ fontSize: 14, color: "text.disabled" }} />
              </InputAdornment>
            ),
            endAdornment: value ? (
              <InputAdornment position="end">
                <IconButton size="small" onClick={handleClear} tabIndex={-1}>
                  <ClearIcon sx={{ fontSize: 13 }} />
                </IconButton>
              </InputAdornment>
            ) : null,
          }}
          sx={{
            minWidth: 165,
            width: 165,
            cursor: "pointer",
            "& .MuiOutlinedInput-notchedOutline": { borderColor: open ? "#1976d2" : "#dde8fb" },
            "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: "#90caf9" },
            "& .MuiOutlinedInput-root": { cursor: "pointer" },
            "& .MuiOutlinedInput-root.Mui-focused": { boxShadow: "none !important", outline: "none !important" },
            "& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline": { borderColor: open ? "#1976d2" : "#dde8fb" },
            "& input": { cursor: "pointer" },
            "& input:focus": { outline: "none !important", boxShadow: "none !important" },
          }}
        />

        {open && (
          <Paper
            elevation={4}
            sx={{
              position: "absolute",
              ...(dropUp ? { bottom: "100%", mb: "4px" } : { top: "100%", mt: "4px" }),
              left: 0,
              zIndex: 1400,
              width: 165, // ← matches text field width exactly
              borderRadius: "10px",
              border: "1px solid #dde8fb",
              overflow: "hidden",
              boxShadow: "0px 4px 20px rgba(25, 118, 210, 0.15)",
            }}
          >
            {/* Month/Year header */}
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                px: 1,
                py: 0.75,
                bgcolor: "#f7faff",
                borderBottom: "1px solid #eaf0fb",
              }}
            >
              <IconButton size="small" onClick={prevMonth} sx={{ p: 0.25 }}>
                <ChevronLeftIcon sx={{ fontSize: 16 }} />
              </IconButton>
              <Typography
                sx={{ fontSize: "11px", fontWeight: 700, color: "primary.main", cursor: "pointer" }}
                onClick={goToday}
              >
                {MONTHS[viewMonth].slice(0, 3)} {viewYear}
              </Typography>
              <IconButton size="small" onClick={nextMonth} sx={{ p: 0.25 }}>
                <ChevronRightIcon sx={{ fontSize: 16 }} />
              </IconButton>
            </Box>

            {/* Day headers */}
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: "repeat(7, 1fr)",
                px: 0.5,
                pt: 0.5,
              }}
            >
              {DAYS.map((d) => (
                <Typography
                  key={d}
                  sx={{
                    fontSize: "9px",
                    fontWeight: 700,
                    color: "text.disabled",
                    textAlign: "center",
                    py: 0.25,
                  }}
                >
                  {d}
                </Typography>
              ))}
            </Box>

            {/* Date cells */}
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: "repeat(7, 1fr)",
                px: 0.5,
                pb: 0.5,
                gap: "1px",
              }}
            >
              {cells.map((cell, idx) => {
                const selected = cell.iso === value;
                const disabled = isDisabled(cell.iso);
                const todayCell = isToday(cell.iso);

                return (
                  <Box
                    key={idx}
                    onClick={() => cell.currentMonth && !disabled && handleSelect(cell.iso)}
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      height: "20px",
                      borderRadius: "4px",
                      cursor: cell.currentMonth && !disabled ? "pointer" : "default",
                      bgcolor: selected
                        ? "#1976d2"
                        : todayCell && !selected
                        ? "#e3f2fd"
                        : "transparent",
                      "&:hover": cell.currentMonth && !disabled && !selected
                        ? { bgcolor: "#e8f0fe" }
                        : {},
                    }}
                  >
                    <Typography
                      sx={{
                        fontSize: "10px",
                        fontWeight: selected ? 700 : todayCell ? 700 : 400,
                        color: selected
                          ? "#fff"
                          : !cell.currentMonth || disabled
                          ? "#ccc"
                          : todayCell
                          ? "#1976d2"
                          : "text.primary",
                        lineHeight: 1,
                      }}
                    >
                      {cell.day}
                    </Typography>
                  </Box>
                );
              })}
            </Box>

            {/* Footer */}
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                px: 1,
                py: 0.5,
                borderTop: "1px solid #eaf0fb",
                bgcolor: "#f7faff",
              }}
            >
              <Typography
                sx={{ fontSize: "10px", color: "#c62828", cursor: "pointer", fontWeight: 500 }}
                onClick={() => { onChange(""); setOpen(false); }}
              >
                Clear
              </Typography>
              <Typography
                sx={{ fontSize: "10px", color: "primary.main", cursor: "pointer", fontWeight: 500 }}
                onClick={() => {
                  const iso = isoString(today.getFullYear(), today.getMonth(), today.getDate());
                  if (!isDisabled(iso)) { onChange(iso); setOpen(false); }
                  else goToday();
                }}
              >
                Today
              </Typography>
            </Box>
          </Paper>
        )}
      </Box>
    </ClickAwayListener>
  );
}